import { Button, Spinner } from '@/common/ui';
import { useStripe, useElements, CardElement, Elements } from '@stripe/react-stripe-js';
import * as S from './BillingInfo.styles';
import { loadStripe } from '@stripe/stripe-js/pure';
import { STRIPE_KEY } from '@/common/app/constants/keys';
import { useEffect, useState } from 'react';
import {
    TPayStatus,
    TPayload,
    TPaymentHandler,
    TStripeTokenRes,
} from '../../domain/CheckoutPage.domain';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { LoadingMessage } from '../LoadingMessage/LoadingMessage';
import desktopLoader from '@/shared/images/Images/desktop_loader.png';
import { SpecialForm } from '../SpecialForm/SpecialForm';
import { getCS, getLS, LocalStorageTypes } from '@/common/service/storage';
import { ExternalUserValidationError } from '@/common/app/utils/errorsUtils';
import { postPay } from '@/entities/Checkout/service/postPay';
import { TPayResponseItem } from '@/entities/Checkout/domain/Checkout.domain';
import { createAffId } from '@/common/app/utils/affiliates';
import { AgreementInfo } from './AgreementInfo';
import { PayPal } from './PayPal';
import { CustomThemeNames } from '@/styles/Themes/Theme.domain';
import { Stripe } from '@stripe/stripe-js';

type TStripeProps = {
    userId?: string;
    sessionId?: string;
    sendSms?: boolean;
    checkoutLoading: boolean;
    showPayPal: boolean;
    totalAmount: number;
    onSuccessfullyPayment: (accessKey: string) => void;
    stopTimer?: () => void;
};

const StripeForm = ({
    userId,
    sessionId,
    sendSms,
    checkoutLoading,
    onSuccessfullyPayment,
    stopTimer,
    totalAmount,
    showPayPal,
}: TStripeProps) => {
    const stripe = useStripe();
    const elements = useElements();
    const clientTheme = getLS(LocalStorageTypes.LS_THEME_MODE);

    const isDark = clientTheme === CustomThemeNames.dark;

    const [status, setStatus] = useState<TPayStatus>({
        canMakePayment: false,
        isLoading: false,
        isError: false,
        errorMessage: '',
        errors: [],
        specialValue: '',
        unmounted: false,
        isActiveButton: true,
    });

    const getPayload = async ({
        isSpecial,
        paypalOrderId,
    }: {
        isSpecial?: boolean;
        paypalOrderId?: string;
    }): Promise<TStripeTokenRes> => {
        if (isSpecial) {
            return {
                special_row: status.specialValue,
            };
        }

        if (paypalOrderId) {
            return {
                paypal_order_id: paypalOrderId,
                special_row: status.specialValue,
            };
        }

        if (!stripe || !elements) {
            throw new Error('Stripe Error.');
        }

        const stripeCardElement = elements.getElement(CardElement);

        if (!stripeCardElement) {
            throw new Error('Please select your payment option.');
        }

        const { token, error } = await stripe.createToken(stripeCardElement);

        if (!token || error) {
            throw error || new Error('Stripe Token Error');
        }

        return {
            stripe_token: token.id,
            special_row: status.specialValue,
        };
    };

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const paymentHandler = async (
        { isSpecial, paypalOrderId }: { isSpecial: boolean; paypalOrderId?: string },
        cb: TPaymentHandler
        // eslint-disable-next-line sonarjs/cognitive-complexity
    ) => {
        try {
            const payloadPart = await getPayload({ isSpecial, paypalOrderId });

            const affiliate_id = createAffId();

            const payload: TPayload = {
                ...payloadPart,
                session_id: sessionId || '',
                user_id: userId || '',
                send_sms: sendSms ?? false,
                affiliate_id,
            };

            const jamcom = getCS('jamcom');
            const jamtracker = getCS('jamtracker');

            if (jamcom && jamtracker) {
                payload.jamcom = `${jamcom}`;
                payload.jamtracker = `${jamtracker}`;
            }

            try {
                const res = await postPay(payload);
                if (res.success) {
                    return cb(null, res.data);
                } else {
                    const pickStringErrors = () => {
                        const errors = res.errors || res.data.errors || [];
                        if (errors && Array.isArray(errors)) {
                            const e = errors.filter((error) => typeof error === 'string');
                            if (e.length > 0) {
                                return e;
                            }
                        }
                        return ['Something Error'];
                    };
                    const errors = res.errors || res.data.errors || [];
                    throw new ExternalUserValidationError(pickStringErrors().join(', '), errors);
                }
            } catch (err) {
                if (
                    err instanceof Error &&
                    err.message !== 'Network Error' &&
                    err.message !== 'timeout of 30000ms exceeded'
                ) {
                    return cb(err);
                }
            }
        } catch (err) {
            return cb(err as Error);
        }
    };

    const submitHandler = async ({
        isSpecial,
        paypalOrderId,
    }: {
        isSpecial: boolean;
        paypalOrderId?: string;
    }) => {
        try {
            setStatus({ ...status, isLoading: true, isError: false, errorMessage: '' });
            stopTimer?.();

            const response = await new Promise<TPayResponseItem>((res, rej) =>
                paymentHandler({ isSpecial, paypalOrderId }, (err, data) =>
                    !data ? rej(err) : res(data)
                )
            );

            onSuccessfullyPayment(response.accesskey);

            setStatus({ ...status, isLoading: false });

            return 'success';
        } catch (err) {
            console.log(err);

            setStatus({
                ...status,
                isLoading: false,
                isError: true,
                errorMessage: err instanceof Error ? err.message : 'Unknown Error',
                errors:
                    err instanceof ExternalUserValidationError && Array.isArray(err.errors)
                        ? (err.errors as string[])
                        : ([] as string[]),
            });

            return 'fail';
        }
    };

    return (
        // eslint-disable-next-line react/jsx-filename-extension
        <div style={{ position: 'relative' }}>
            <SpecialForm
                value={status.specialValue}
                onSubmit={() => submitHandler({ isSpecial: true })}
                onChange={(value) => {
                    setStatus({ ...status, specialValue: value });
                }}
                isLoading={status.isLoading}
            />
            {!status.canMakePayment && (
                <S.StripeLoader>
                    <S.ImgLoader src={desktopLoader.src} alt="loader" />
                </S.StripeLoader>
            )}
            <S.StripeCardContainer>
                <CardElement
                    options={S.StripeStyles}
                    onReady={() => setStatus({ ...status, canMakePayment: true })}
                />
            </S.StripeCardContainer>
            {showPayPal && !!sessionId && (
                <PayPal
                    totalAmount={totalAmount}
                    isDark={isDark}
                    disabled={status.isLoading || !status.canMakePayment || checkoutLoading}
                    sessionId={sessionId}
                    submitHandler={(paypalOrderId) => {
                        return submitHandler({ isSpecial: false, paypalOrderId });
                    }}
                />
            )}
            <AgreementInfo />
            {status.isError && <ErrorMessage />}
            {status.isLoading && <LoadingMessage />}
            <S.BraintreeButton>
                <Button
                    variant={'primary'}
                    disabled={status.isLoading || !status.canMakePayment || checkoutLoading}
                    onClick={() => submitHandler({ isSpecial: false })}
                >
                    {status.isLoading || checkoutLoading ? (
                        <Spinner isLoading />
                    ) : (
                        'Complete Secure Booking'
                    )}
                </Button>
            </S.BraintreeButton>
        </div>
    );
};

export const StripeComponent = (props: TStripeProps) => {
    const [stripePromise, setStripePromise] = useState<Stripe | null>(null);

    useEffect(() => {
        (async () => {
            const stripe = await loadStripe(STRIPE_KEY);

            setStripePromise(stripe);
        })();
    }, []);

    if (!stripePromise) {
        return <></>;
    }

    return (
        <Elements stripe={stripePromise}>
            <StripeForm {...props} />
        </Elements>
    );
};
